// src/pages/AdminCombos.tsx
import React, { useEffect, useState } from 'react';
import { FaBan, FaChartLine, FaDownload, FaHandshake, FaPercent, FaSave, FaSync } from 'react-icons/fa';
import { adminComboAction, formatMoney } from '../lib/combos';

export default function AdminCombos() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>({ stats: {}, combos: [], commission_settings: [] });
  const [comboRate, setComboRate] = useState('8');
  const [message, setMessage] = useState<string | null>(null);

  const refresh = () => {
    setLoading(true);
    adminComboAction('dashboard')
      .then((res: any) => { setData(res); setComboRate(String(res?.settings?.combo_rate ?? 8)); })
      .catch((e) => setMessage(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, []);

  const saveRate = async () => {
    setMessage(null);
    try {
      await adminComboAction('update_commission_setting', { product_type: 'combos', commission_rate: Number(comboRate) });
      setMessage('Commission Combos mise à jour.');
      refresh();
    } catch (e: any) { setMessage(e.message); }
  };

  const suspend = async (comboId: string) => {
    if (!confirm('Suspendre ce Combo?')) return;
    await adminComboAction('suspend_combo', { combo_id: comboId });
    refresh();
  };

  const exportCsv = () => {
    const rows = [['Titre','Statut','Ville','Prix','Valeur','Commission','Ventes','Créé le'], ...data.combos.map((c: any) => [c.title, c.status, c.city || c.region || '', c.suggested_price || 0, c.total_value || 0, c.commission_rate || 8, c.sales_count || 0, c.created_at])];
    const csv = rows.map((r: any[]) => r.map((v) => `"${String(v ?? '').replaceAll('"','""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a'); a.href = url; a.download = 'forfeo-combos.csv'; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-8 animate-in fade-in">
      <div className="rounded-[2rem] bg-gradient-to-br from-slate-950 to-blue-900 text-white p-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div><p className="text-xs font-black uppercase tracking-[0.25em] text-cyan-200 mb-2">Administration</p><h2 className="text-3xl md:text-5xl font-black flex items-center gap-3"><FaHandshake /> Combos collaboratifs</h2><p className="mt-3 text-blue-100 max-w-3xl">Supervision, commissions, ventes, suspension et preuve d’acceptation. L’admin supervise; les marchands acceptent entre eux.</p></div>
          <button onClick={refresh} className="rounded-2xl bg-white/10 border border-white/15 px-5 py-3 font-black"><FaSync className="inline mr-2" /> Actualiser</button>
        </div>
      </div>

      {message && <div className="rounded-2xl bg-blue-50 border border-blue-100 text-blue-900 p-4 font-bold">{message}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        {[['Combos créés', data.stats?.total_combos], ['Actifs', data.stats?.active_combos], ['En attente', data.stats?.pending_combos], ['Ventes', data.stats?.sales_count], ['Commission Forfeo', formatMoney(data.stats?.commission_total || 0)]].map(([label, value]) => (
          <div key={label as string} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm"><p className="text-xs uppercase tracking-widest font-black text-slate-400">{label}</p><p className="mt-2 text-2xl font-black text-slate-900">{value ?? 0}</p></div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[.8fr_1.2fr] gap-6">
        <div className="bg-white rounded-[2rem] border border-slate-200 p-6 shadow-sm">
          <h3 className="text-xl font-black flex items-center gap-2 mb-4"><FaPercent className="text-blue-600" /> Commission globale</h3>
          <p className="text-sm text-slate-600 mb-4">Définis le pourcentage prélevé par Forfeo. Par défaut : 8 % pour les Combos.</p>
          <label className="text-xs font-black uppercase text-slate-400">Commission Combos (%)</label>
          <input type="number" min="0" max="50" step="0.1" value={comboRate} onChange={(e) => setComboRate(e.target.value)} className="mt-2 w-full rounded-2xl border border-slate-200 p-4 font-black text-2xl" />
          <button onClick={saveRate} className="mt-4 w-full rounded-2xl bg-slate-900 text-white py-4 font-black uppercase"><FaSave className="inline mr-2" /> Enregistrer</button>
          <div className="mt-5 rounded-2xl bg-amber-50 border border-amber-100 p-4 text-sm text-amber-950"><b>Protection Forfeo :</b> chaque acceptation marchand conserve la commission, le prix accepté, l’IP, l’horodatage et la version des conditions.</div>
        </div>

        <div className="bg-white rounded-[2rem] border border-slate-200 p-6 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between gap-4 mb-5"><h3 className="text-xl font-black flex items-center gap-2"><FaChartLine className="text-blue-600" /> Combos récents</h3><button onClick={exportCsv} className="rounded-xl bg-slate-100 px-4 py-2 font-black text-sm"><FaDownload className="inline mr-1" /> CSV</button></div>
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase text-slate-400"><th className="p-3">Combo</th><th className="p-3">Statut</th><th className="p-3">Prix</th><th className="p-3">Ventes</th><th className="p-3">Action</th></tr></thead>
              <tbody>
                {(data.combos || []).map((c: any) => (
                  <tr key={c.id} className="border-t border-slate-100"><td className="p-3 font-black">{c.title}<br/><span className="text-xs font-bold text-slate-400">{c.city || c.region}</span></td><td className="p-3"><span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-black">{c.status}</span></td><td className="p-3 font-bold">{formatMoney(c.suggested_price)}</td><td className="p-3 font-bold">{c.sales_count || 0}</td><td className="p-3"><button onClick={() => suspend(c.id)} className="text-red-600 font-black"><FaBan /></button></td></tr>
                ))}
              </tbody>
            </table>
            {!loading && !(data.combos || []).length && <div className="p-8 text-center text-slate-500 font-bold">Aucun Combo pour l’instant.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
